/**
 * The cache lifecycle around one drag: the mutation, the optimistic placement it publishes,
 * the confirmed placement it settles, and the ways a write can come back unapplied.
 *
 * A drop in another cell writes the card's fields. On a board the viewer can reorder, a drop
 * also writes the card's place in the view's manual order: on its own inside the cell the card
 * came from, and after the field write in another cell.
 *
 * It names no endpoint and builds no URL. The transports are
 * `SavedViewsBoardAdapter.moveBoardItem` and `rankBoardItem`, which is what lets a second board
 * reuse all of this by supplying different ones.
 *
 * The overlay lives in the mutation cache rather than the query cache, so a pending move
 * survives the board being re-keyed by a fresh route response: `useOptimisticData` applies
 * it over whatever base the query currently holds.
 */

import {announce} from '@github-ui/aria-live'
import type {BoardItemDragState, BoardItemsMove, BoardSelectedCard, CellAddress} from '@github-ui/board-core'
import {entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import type {EntityMutationVariables} from '@github-ui/optimistic-connection/settlementRegistry'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {type MutationFilters, type QueryKey, useMutationState, useQueryClient} from '@github-ui/react-query'
import {useCallback, useMemo, useState} from 'react'

import {toViewRankTarget} from '../../utils/view-rank-target'
import {heldCells} from './board-document-adapter'
import {BoardMoveFailedError, BoardMoveRefusedError, ViewRankStaleError} from './board-move-errors'
import {
  type GestureBoard,
  movesCardTo,
  readBoardPlacement,
  toConfirmedGestureOperation,
  toOptimisticPlacementOperation,
} from './board-placement'
import {keepMovedCards, movedCardBuckets} from './keep-moved-cards'
import type {RefusedMove} from './refused-move-messages'
import {boardMovesFor, useBoardCellReloads} from './use-board-cell-reloads'
import type {
  CellPageOutcome,
  SavedViewsBoardAdapter,
  SavedViewsBoardData,
  SavedViewsBoardItem,
  SavedViewsBoardMoveResult,
  SavedViewsBoardOptions,
  SavedViewsBoardPayload,
  SavedViewsBoardRankResult,
} from './types'

const CANNOT_MOVE_ITEM = 'You do not have permission to move this item.'
const ALREADY_MOVING = 'Wait for this item’s last move to finish before moving it again.'
const BOARD_BUSY = 'Wait for the last move to finish before moving another item.'
const MOVE_FAILED = 'Could not move this item. Please try again.'
const LEFT_THE_VIEW = 'Moved. This item no longer matches this view and will not appear on the next refresh.'
const CARDS_CHANGED = 'The cards here changed and were reloaded. Try the move again.'
const MOVED_WITHOUT_POSITION = 'Moved, but could not save its position.'
const POSITION_NOT_SAVED = `${MOVED_WITHOUT_POSITION} Try again.`
const MOVED_CARDS_CHANGED = `${MOVED_WITHOUT_POSITION} The cards here changed and were reloaded. Try again.`
const LANDED_ELSEWHERE = 'Moved, but not to the cell it was dropped in, so its position was not saved.'

/** What one gesture wrote. */
type BoardGestureResult = {
  /** What the field write did. Absent for a gesture inside one cell, which changes no field. */
  moved?: SavedViewsBoardMoveResult
  /** The rank write's answer. Absent for a gesture that wrote no rank. */
  ranked?: SavedViewsBoardRankResult
  /** Why the rank write failed after the field write stood, which keeps the card in its new cell. */
  rankError?: unknown
  /** Whether the field write put the card outside the drop cell, so no rank was sent. */
  landedElsewhere?: boolean
}

type RefreshCell<TItem extends SavedViewsBoardItem> = (
  address: CellAddress,
  reconcile?: (page: readonly TItem[], held: readonly TItem[]) => readonly TItem[],
) => Promise<CellPageOutcome>

/**
 * What the last move left for the host to show: a dialog when the move did not happen, whether
 * the server refused it or it failed, and a banner for a move that applied but left the view.
 * The dialog keeps the card the move started from, so dismissing it can return focus there.
 */
export type BoardMoveFeedback =
  | {kind: 'banner'; message: string}
  | {kind: 'dialog'; refusal: RefusedMove; sourceCard: BoardSelectedCard}

export type UseBoardItemMoveResult = {
  /**
   * Handed to the board only on a board that moves cards at all. Absent is what makes a
   * board read-only: no card can be picked up and no cell is a drop target.
   */
  onItemsMove?: (move: BoardItemsMove) => void
  getItemDragState: (item: SavedViewsBoardItem) => BoardItemDragState
  /**
   * Why a card can never be moved on this board, for the card to write out. Leaves out a
   * move in flight, which `getItemDragState` still refuses: it clears within moments, and
   * text added and removed on every move is noise.
   */
  getMoveRefusal: (item: SavedViewsBoardItem) => string | undefined
  canDropItem: (item: SavedViewsBoardItem, move: {from: CellAddress; to: CellAddress}) => boolean
  /** What the last write said when it did not apply, for the host to draw. */
  moveFeedback?: BoardMoveFeedback
  clearMoveFeedback: () => void
}

export function useBoardItemMove<TItem extends SavedViewsBoardItem>({
  adapter,
  options,
  boardData,
  boardId,
  queryKey,
  canReorder = false,
  refreshCell,
  onBoardWrite,
}: {
  adapter: Pick<SavedViewsBoardAdapter<TItem>, 'moveBoardItem' | 'rankBoardItem' | 'viewKey'>
  options: SavedViewsBoardOptions
  boardData: SavedViewsBoardData<TItem>
  /** Identifies the board, so what this remembers about its moves starts over with a new one. */
  boardId: string
  /** The key the board's cards are cached under, which a write reads its confirmed cells from. */
  queryKey: QueryKey
  /**
   * Whether a drop inside a cell can put a card in a new place in the view's order. Every card
   * can then be picked up, and a card that can change no field stays in its own cell.
   */
  canReorder?: boolean
  /** Reloads one cell, for a rank write whose cards changed since they were read. */
  refreshCell?: RefreshCell<TItem>
  /**
   * Called twice for one move: a read from before the gesture and one from during the write
   * are both stale, and the two windows do not overlap. Not called when a write fails.
   */
  onBoardWrite?: () => void
}): UseBoardItemMoveResult {
  const [moveFeedback, setMoveFeedback] = useState<BoardMoveFeedback | undefined>(undefined)
  /**
   * The cards with a write in flight.
   *
   * A second move of the same card while the first is still going is the one case the
   * overlay cannot recover from: two writes to one field settle in whatever order the
   * responses arrive, the later fold wins the cache, and this board does not refetch, so a
   * board left on the earlier value stays there. Refusing the second gesture keeps that from
   * happening.
   */
  const movingItemIds = usePendingBoardMoves()
  const {columnField, rowField, viewerCanMoveBetweenColumns, viewerCanMoveBetweenRows} = boardData
  const {moveBoardItem, rankBoardItem, viewKey} = adapter

  const queryClient = useQueryClient()
  // The cards each board moved, by board id, which outlive this board as its owed reloads do.
  const moves = boardMovesFor(queryClient)
  const thisBoard = useMemo<GestureBoard>(() => ({id: boardId, queryKey}), [boardId, queryKey])

  const report = useCallback((message: string) => {
    setMoveFeedback({kind: 'banner', message})
    announce(message)
  }, [])

  const rememberWrites = useCallback(
    (board: string, itemId: string, {moved}: BoardGestureResult) => {
      if (moved?.itemId !== itemId || moved.results.length === 0) return
      moves.remember(board, itemId, movedCardBuckets(moved.cells, {hasRows: rowField != null}))
    },
    [moves, rowField],
  )

  const readCell = useMemo(
    () =>
      refreshCell &&
      ((address: CellAddress) =>
        refreshCell(address, (page, held) => keepMovedCards({page, held, cell: address, moved: moves.of(boardId)}))),
    [boardId, moves, refreshCell],
  )
  // Reloads still owed, which hold every pickup on a board the viewer can reorder.
  const {reloadCell, isReloading} = useBoardCellReloads({boardId, readCell})

  /**
   * Reloads the cells of `board` other than `except` that show one of `itemIds`. A card has one
   * rank in the view, so a rank moves it in every cell it is in, and only a new read says where.
   * Read from the cache, which holds the confirmed write before the board draws it.
   */
  const reloadOtherCopies = useCallback(
    (board: GestureBoard, itemIds: readonly string[], except: CellAddress) => {
      const ranked = new Set(itemIds)
      const cells = queryClient.getQueryData<SavedViewsBoardPayload<TItem, unknown>>(board.queryKey)?.boardData.data
        .cells
      for (const cell of cells ?? []) {
        if (cell.columnKey === except.columnKey && cell.rowKey === except.rowKey) continue
        if (cell.items.some(item => ranked.has(item.globalId))) reloadCell(cell, board.id)
      }
    },
    [queryClient, reloadCell],
  )

  const {mutate} = useEntityMutation<BoardGestureResult>({
    mutationFn: async operation => {
      const gesture = readBoardPlacement(operation)?.gesture
      if (gesture === undefined || columnField == null) throw new BoardMoveFailedError()

      const sameCell = gesture.from.columnKey === gesture.to.columnKey && gesture.from.rowKey === gesture.to.rowKey
      let moved: SavedViewsBoardMoveResult | undefined
      if (!sameCell) {
        if (moveBoardItem === undefined) throw new BoardMoveFailedError()
        // A failed field write fails the whole gesture, so no rank is sent for a move that did not happen.
        moved = await moveBoardItem({
          itemId: operation.id,
          from: gesture.from,
          to: gesture.to,
          arrangement: {columnField, rowField},
          query: options.query,
        })
      }
      if (gesture.rank === undefined || moved?.changedFieldMatchesView === false) return {moved}
      // A server that normalized the value can leave the card outside the cell whose cards the rank names.
      if (moved !== undefined && !movesCardTo(moved, {itemId: operation.id, to: gesture.to})) {
        return {moved, landedElsewhere: true}
      }
      if (rankBoardItem === undefined) throw new BoardMoveFailedError()

      try {
        return {moved, ranked: await rankBoardItem({itemId: operation.id, ...gesture.rank})}
      } catch (error) {
        // The field write already stands, so only the position is lost.
        if (moved === undefined) throw error
        return {moved, rankError: error}
      }
    },
    // Built from the writes rather than from the drag, so a server that normalised the value,
    // clipped it or left it alone is what the board settles to. A response naming another
    // card folds onto nothing, so it settles nothing either.
    getConfirmedOperation: (result, operation) => toConfirmedGestureOperation(operation, result),
    // Runs after the board that made the gesture may have been replaced, so what follows a write
    // goes to that board rather than to the one on screen.
    onSuccess: (result, {operation}) => {
      onBoardWrite?.()
      const gesture = readBoardPlacement(operation)?.gesture
      const board = gesture?.board ?? thisBoard
      rememberWrites(board.id, operation.id, result)
      const placement = readBoardPlacement(operation)
      if (result.ranked?.itemId === operation.id && gesture !== undefined) {
        reloadOtherCopies(board, placement?.ranked ?? [operation.id], gesture.to)
      }
      const {rankError} = result
      if (rankError instanceof ViewRankStaleError) {
        report(MOVED_CARDS_CHANGED)
        if (gesture !== undefined) reloadCell(gesture.to, board.id)
        return
      }
      if (rankError !== undefined) {
        report(
          rankError instanceof BoardMoveRefusedError
            ? `${MOVED_WITHOUT_POSITION} ${rankError.message}`
            : POSITION_NOT_SAVED,
        )
        return
      }
      if (result.landedElsewhere === true) {
        report(LANDED_ELSEWHERE)
        return
      }
      if (result.moved?.changedFieldMatchesView !== false) return
      // The card's new value contradicts the view's own search, so it has left the board.
      // Saying so is the honest minimum: the card stays where the record now puts it, and
      // the next board read drops it. The departure treatment is github/views#84.
      report(LEFT_THE_VIEW)
    },
    onError: (error, {operation}) => {
      const gesture = readBoardPlacement(operation)?.gesture
      if (error instanceof ViewRankStaleError) {
        report(CARDS_CHANGED)
        if (gesture !== undefined) reloadCell(gesture.to, (gesture.board ?? thisBoard).id)
        return
      }
      // A refusal is shown in the server's own words and a failure in ours. The overlay
      // drops either way, and it drops whole: one gesture is one operation, so a refused
      // diagonal move takes both halves with it rather than leaving the card in a cell that
      // is neither where it was nor where it was dropped.
      if (!gesture) {
        report(error instanceof BoardMoveRefusedError ? error.message : MOVE_FAILED)
        return
      }
      const refusal: RefusedMove =
        error instanceof BoardMoveRefusedError
          ? {
              message: error.message,
              field: error.field,
              code: error.code,
              details: error.details,
              targetLabel: targetLabel(boardData, error.field, gesture.to),
            }
          : {message: MOVE_FAILED, details: {}}
      setMoveFeedback({kind: 'dialog', refusal, sourceCard: {...gesture.from, itemId: operation.id}})
    },
  })

  const canMoveFields =
    (viewerCanMoveBetweenColumns === true || viewerCanMoveBetweenRows === true) && moveBoardItem !== undefined
  const canMoveItems = (canMoveFields || canReorder) && columnField != null

  /**
   * Where a drop puts the card in the view's order: the rank to write and the place to draw it,
   * `'stale'` when the cards on screen cannot say, or `undefined` when the drop writes no rank.
   */
  const rankDrop = useCallback(
    (itemId: string, {to, anchor}: BoardItemsMove) => {
      if (!canReorder || anchor === undefined || columnField == null) return undefined

      const cell = boardData.data.cells.find(
        candidate => candidate.columnKey === to.columnKey && candidate.rowKey === to.rowKey,
      )
      const items = cell?.items ?? []
      const rank = toViewRankTarget({
        cards: items.map(item => ({id: item.globalId, ranked: item.ranked === true})),
        itemId,
        anchor,
        hasMoreCards: cell?.hasMoreItems ?? false,
        guessed: new Set(items.filter(item => item.positionGuessed === true).map(item => item.globalId)),
      })
      if (rank === 'none') return undefined
      if (rank === 'stale') return rank

      return {
        rank,
        position: {cell: to, anchor, arrangement: {columnField, rowField}},
        ranked: [...rank.rankFirst, itemId],
        view: viewKey,
      }
    },
    [boardData, canReorder, columnField, rowField, viewKey],
  )

  const onItemsMove = useCallback(
    (move: BoardItemsMove) => {
      // A drop carries exactly one card. Moving a selection is not offered, and applying
      // part of a list nobody can produce would be worse than refusing it.
      const [itemId] = move.itemIds
      if (itemId === undefined || move.itemIds.length !== 1 || columnField == null) return
      if (isLocked(movingItemIds, itemId, canReorder, isReloading)) return

      const sameCell = move.from.columnKey === move.to.columnKey && move.from.rowKey === move.to.rowKey
      const placed = rankDrop(itemId, move)
      if (sameCell && placed === undefined) return
      if (placed === 'stale') {
        // In another cell the field write still means what it did, so it goes ahead alone.
        if (sameCell) {
          report(CARDS_CHANGED)
          reloadCell(move.to)
          return
        }
      }

      setMoveFeedback(undefined)
      onBoardWrite?.()
      mutate(
        {
          operation: toOptimisticPlacementOperation({
            itemId,
            from: move.from,
            to: move.to,
            arrangement: {columnField, rowField},
            // What the card holds on an axis the gesture did not cross has to survive the
            // move, and the board is the only thing that knows it.
            held: heldCells(boardData, itemId),
            ...(placed !== 'stale' && placed),
            board: thisBoard,
          }),
        },
        // The board-wide lock leaves this the only write in flight, so its own callback runs.
        placed === 'stale'
          ? {
              onSuccess: ({moved}) => {
                if (moved?.changedFieldMatchesView === false) return
                if (moved !== undefined && !movesCardTo(moved, {itemId, to: move.to})) {
                  report(LANDED_ELSEWHERE)
                  return
                }
                report(MOVED_CARDS_CHANGED)
                reloadCell(move.to, thisBoard.id)
              },
            }
          : undefined,
      )
    },
    [
      boardData,
      canReorder,
      columnField,
      movingItemIds,
      mutate,
      onBoardWrite,
      rankDrop,
      isReloading,
      reloadCell,
      report,
      rowField,
      thisBoard,
    ],
  )

  const getMoveRefusal = useCallback(
    (item: SavedViewsBoardItem): string | undefined =>
      canReorder || item.canMoveBetweenColumns === true || item.canMoveBetweenRows === true
        ? undefined
        : CANNOT_MOVE_ITEM,
    [canReorder],
  )

  const getItemDragState = useCallback(
    (item: SavedViewsBoardItem): BoardItemDragState => {
      const refusal = getMoveRefusal(item)
      if (refusal !== undefined) return {draggable: false, reason: refusal}
      if (movingItemIds.has(item.globalId)) return {draggable: false, reason: ALREADY_MOVING}
      if (isLocked(movingItemIds, item.globalId, canReorder, isReloading)) {
        return {draggable: false, reason: BOARD_BUSY}
      }

      return {draggable: true}
    },
    [canReorder, getMoveRefusal, isReloading, movingItemIds],
  )

  /**
   * A drop that crosses an axis the card cannot be written on is refused before it is made. A
   * card in several rows dragged into another of them still crosses rows: the write would
   * change the row field, and the server refuses that.
   */
  const canDropItem = useCallback(
    (item: SavedViewsBoardItem, {from, to}: {from: CellAddress; to: CellAddress}): boolean =>
      canMoveFields &&
      (from.columnKey === to.columnKey || item.canMoveBetweenColumns === true) &&
      (from.rowKey === to.rowKey || item.canMoveBetweenRows === true),
    [canMoveFields],
  )

  const clearMoveFeedback = useCallback(() => setMoveFeedback(undefined), [])

  return {
    onItemsMove: canMoveItems ? onItemsMove : undefined,
    getItemDragState,
    getMoveRefusal,
    canDropItem,
    moveFeedback,
    clearMoveFeedback,
  }
}

/**
 * Whether a card has to wait for a move in flight. On a board the viewer can reorder, every card
 * waits for any move and any cell reload: a rank request names the ranked cards around the drop,
 * and one built before they settle can name a rank the server never wrote or a card drawn where
 * it no longer sorts.
 */
function isLocked(
  movingItemIds: ReadonlySet<string>,
  itemId: string,
  canReorder: boolean,
  isReloading: boolean,
): boolean {
  return canReorder ? movingItemIds.size > 0 || isReloading : movingItemIds.has(itemId)
}

/** What the board calls the value a move asked `field` to take, so a refusal can name it as the reader saw it. */
function targetLabel(
  {columnField, rowField, data}: SavedViewsBoardData<SavedViewsBoardItem>,
  field: string | undefined,
  to: CellAddress,
): string | undefined {
  if (field === undefined) return undefined
  if (field === columnField) return data.columns.find(column => column.key === to.columnKey)?.label
  if (field === rowField) return data.rows.find(row => row.key === to.rowKey)?.label
  return undefined
}

/** Every board move in the mutation cache, made from any board on the page. */
export const BOARD_MOVES: MutationFilters = {
  mutationKey: entityFieldsMutationKey(),
  predicate: mutation => isBoardMove(mutation.state.variables),
}

/**
 * The ids of every card with a board move pending anywhere on the page.
 *
 * Read from the mutation cache rather than held in this board, because the overlay lives
 * there too: a board that remounts, or a second board showing the same card, has to refuse
 * the card for as long as the overlay holds it. A move leaves `pending` only after
 * `useEntityMutation` folds the write into the cache.
 */
function usePendingBoardMoves(): ReadonlySet<string> {
  const itemIds = useMutationState<string>({
    // Filtered here rather than in `select`, so a field edit elsewhere on the page leaves the
    // result identical and redraws no card on this board.
    filters: {...BOARD_MOVES, status: 'pending'},
    select: mutation => (mutation.state.variables as EntityMutationVariables).operation.id,
  })

  return useMemo(() => new Set(itemIds), [itemIds])
}

function isBoardMove(variables: unknown): variables is EntityMutationVariables {
  const operation = (variables as EntityMutationVariables | undefined)?.operation
  return operation !== undefined && readBoardPlacement(operation) !== undefined
}
